// @ts-nocheck

const GWP_PROPERTY_KEY = '_laarchive_gwp';
const GWP_PROPERTY_VALUE = '330989';

class CartGift extends HTMLElement {
  connectedCallback() {
    this.noticeEl = this.querySelector('[data-gift-notice]');
    this.addButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-gift-add]'));
    this.removeButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-gift-remove]'));
    this.variantInputs = Array.from(this.querySelectorAll('input[name="gift-variant"]'));
    this.optionInputs = Array.from(this.querySelectorAll('[data-gift-option]'));
    this.hiddenVariantInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-gift-variant-hidden]'));
    this.priceCompareText = this.querySelector('[data-gift-price-compare]');
    this.currency = this.dataset.currency || 'USD';
    this.sectionsParam = this.dataset.sections || 'cart-drawer,header';
    this.thresholdCents = parseInt(this.dataset.threshold || '0', 10);
    this.propertyKey = this.dataset.giftPropertyKey || GWP_PROPERTY_KEY;
    this.propertyValue = this.dataset.giftPropertyValue || GWP_PROPERTY_VALUE;
    this.pending = false;
    this.autoRemoving = false;
    this.variants = [];

    try {
      this.variants = JSON.parse(this.dataset.variants || '[]');
    } catch (e) {
      console.warn('Cart gift: unable to parse variants data', e);
      this.variants = [];
    }

    this.variantInputs.forEach((input) => {
      input.addEventListener('change', () => this.updatePricingDisplay(input));
    });

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.handleOptionChange());
    });

    if (this.addButton) {
      this.addButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleAddGift();
      });
    }

    if (this.removeButton) {
      this.removeButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleRemoveGift();
      });
    }

    document.addEventListener('theme:cart-update', this.handleCartUpdateEvent);
  document.addEventListener('cart:update', this.handleCartUpdateEvent);
    document.addEventListener('cart:updated', this.handleCartUpdateEvent);

    this.collapseComponent();
    this.applyVisibilityFromDataset();
    this.updatePricingDisplay();
    this.handleOptionChange();
    this.handleCartUpdateEvent();
  }

  disconnectedCallback() {
    document.removeEventListener('theme:cart-update', this.handleCartUpdateEvent);
  document.removeEventListener('cart:update', this.handleCartUpdateEvent);
    document.removeEventListener('cart:updated', this.handleCartUpdateEvent);
  }

  handleCartUpdateEvent = async () => {
    const cart = await this.fetchCart();
    if (!cart) {
      this.applyVisibilityFromDataset();
      return;
    }

    const total = cart.total_price ?? cart.totalPrice ?? 0;
    const hasGift = this.doesCartContainGift(cart);

    if (hasGift && total < this.thresholdCents) {
      await this.removeGiftIfPresent(cart);
      this.collapseComponent();
      return;
    }

    if (hasGift || total < this.thresholdCents) {
      this.collapseComponent();
      return;
    }

    this.showComponent();
  };

  get selectedVariantInput() {
    if (this.variantInputs.length) {
      return this.variantInputs.find((input) => input.checked && !input.disabled) ||
        this.variantInputs.find((input) => !input.disabled) ||
        null;
    }
    return this.hiddenVariantInput;
  }

  formatMoney(value) {
    const cents = parseInt(value || '0', 10);
    const hasDecimals = cents % 100 !== 0;

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0,
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(hasDecimals ? 2 : 0)}`;
    }
  }

  updatePricingDisplay(targetInput) {
    const input = targetInput || this.selectedVariantInput;
    if (!input) return;

    const { price, variantTitle } = input.dataset;

    if (this.priceCompareText) {
      this.priceCompareText.textContent = price ? this.formatMoney(price) : '';
    }

    if (variantTitle) {
      const summaryValue = this.querySelector('.cart-gift__selection-value');
      if (summaryValue) {
        summaryValue.textContent = variantTitle;
      }
    }
  }

  handleOptionChange() {
    if (!this.variants.length || !this.hiddenVariantInput) return;

    // First, update which option values are available given current selections
    this.updateOptionAvailability();

    const selectedOptions = this.getSelectedOptions();
    const match = this.findVariantMatch(selectedOptions);

    if (match) {
      this.hiddenVariantInput.value = match.id;
      this.hiddenVariantInput.dataset.price = match.price;
      this.hiddenVariantInput.dataset.compareAt = match.compare_at_price;
      this.hiddenVariantInput.dataset.variantTitle = match.title;
      this.hiddenVariantInput.disabled = !match.available;
      this.setNotice('');
      this.updatePricingDisplay(this.hiddenVariantInput);
    } else {
      this.hiddenVariantInput.value = '';
      this.hiddenVariantInput.dataset.price = '';
      this.hiddenVariantInput.dataset.compareAt = '';
      this.hiddenVariantInput.dataset.variantTitle = '';
      this.hiddenVariantInput.disabled = true;
      this.setNotice('This combination is unavailable.');
    }
  }

  getSelectedOptions(excludePosition) {
    const selected = {};
    this.optionInputs.forEach((input) => {
      const pos = input.dataset.optionPosition;
      if (excludePosition && pos === excludePosition) return;
      if (input.checked) {
        selected[`option${pos}`] = input.value;
      }
    });
    return selected;
  }

  findVariantMatch(selectedOptions) {
    return this.variants.find((variant) => {
      return Object.entries(selectedOptions).every(([key, val]) => {
        const v = variant[key];
        return v && v.toString().toLowerCase() === val.toString().toLowerCase();
      });
    });
  }

  updateOptionAvailability() {
    if (!this.optionInputs.length || !this.variants.length) return;

    // Group inputs by option position
    const positions = [...new Set(this.optionInputs.map((i) => i.dataset.optionPosition))];

    positions.forEach((pos) => {
      const group = this.optionInputs.filter((i) => i.dataset.optionPosition === pos);
      const selectedExcludingGroup = this.getSelectedOptions(pos);

      group.forEach((input) => {
        const candidateSelection = { ...selectedExcludingGroup, [`option${pos}`]: input.value };
        const available = this.variants.some((variant) => {
          const matches = Object.entries(candidateSelection).every(([key, val]) => {
            const v = variant[key];
            return v && v.toString().toLowerCase() === val.toString().toLowerCase();
          });
          return matches && variant.available;
        });

        input.disabled = !available;
        input.parentElement?.classList.toggle('is-disabled', !available);

        // If the currently checked option becomes unavailable, uncheck it
        if (!available && input.checked) {
          input.checked = false;
        }
      });

      // Ensure at least one option in the group is selected
      if (!group.some((i) => i.checked) ) {
        const firstAvailable = group.find((i) => !i.disabled);
        if (firstAvailable) {
          firstAvailable.checked = true;
        }
      }
    });
  }

  async handleAddGift() {
    if (this.pending) return;

    const variantInput = this.selectedVariantInput;
    if (!variantInput) {
      this.setNotice('Select a variant to continue.');
      return;
    }

    this.setPendingState(true);

  const sections = this.sectionsParam;
  const sectionsUrl = window.location.pathname;

  const formData = new FormData();
  formData.append('id', variantInput.value);
  formData.append('quantity', '1');
  formData.append(`properties[${this.propertyKey}]`, this.propertyValue);
  formData.append('sections', sections);
  formData.append('sections_url', sectionsUrl);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.description || 'Unable to add the gift right now.');
      }

  await this.refreshSections(data);
  this.collapseComponent();
  this.setNotice('Gift added to your bag!', true);
    } catch (error) {
      console.error('Cart gift add error:', error);
      this.setNotice(error?.message || 'Something went wrong. Please try again.');
    } finally {
      this.setPendingState(false);
    }
  }

  collapseComponent() {
    this.classList.remove('cart-gift--expanded');
    this.classList.add('cart-gift--collapsed');
    this.setAttribute('aria-hidden', 'true');
    if (this.addButton) {
      this.addButton.removeAttribute('data-added');
    }
  }

  showComponent() {
    requestAnimationFrame(() => {
      this.classList.remove('cart-gift--collapsed');
      this.classList.add('cart-gift--expanded');
      this.removeAttribute('aria-hidden');
    });
  }

  applyVisibilityFromDataset() {
    const unlocked = this.dataset.unlocked === 'true';
    const hasGift = this.dataset.hasGift === 'true';

    if (!unlocked || hasGift) {
      this.collapseComponent();
      return;
    }

    this.showComponent();
  }

  async handleRemoveGift() {
    if (this.pending) return;

    const lineKey = this.dataset.lineKey;
    if (!lineKey) return;

    this.setPendingState(true);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: this.sectionsParam,
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      if (!response.ok || data?.status >= 400) {
        throw new Error(data?.description || 'Unable to remove the gift right now.');
      }

      await this.refreshSections(data);
      this.setNotice('Gift removed.', true);
    } catch (error) {
      console.error('Cart gift remove error:', error);
      this.setNotice(error?.message || 'Unable to remove the gift right now.');
    } finally {
      this.setPendingState(false);
    }
  }

  doesCartContainGift(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some((item) => this.lineHasGiftProperty(item));
  }

  lineHasGiftProperty(item) {
    if (!item || !item.properties) return false;
    const entries = [];

    if (Array.isArray(item.properties)) {
      item.properties.forEach((prop) => {
        if (Array.isArray(prop) && prop.length >= 2) {
          entries.push([prop[0], prop[1]]);
        } else if (prop && typeof prop === 'object') {
          entries.push([prop.name ?? prop.key, prop.value]);
        }
      });
    } else {
      entries.push(...Object.entries(item.properties));
    }

    return entries.some(([key, value]) => key === this.propertyKey && String(value) === String(this.propertyValue));
  }

  async removeGiftIfPresent(cart) {
    if (this.autoRemoving) return;
    const lineItem = cart.items?.find((item) => this.lineHasGiftProperty(item));
    if (!lineItem) return;

    try {
      this.autoRemoving = true;
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineItem.key,
          quantity: 0,
          sections: this.sectionsParam,
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      await this.refreshSections(data);
    } catch (error) {
      console.error('Failed to auto-remove gift below threshold', error);
    } finally {
      this.autoRemoving = false;
    }
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Cart request failed');
      }
      return await response.json();
    } catch (error) {
      console.error('Cart gift: unable to fetch cart data', error);
      return null;
    }
  }

  setPendingState(isPending) {
    this.pending = isPending;
    const targetButtons = [this.addButton, this.removeButton].filter(Boolean);
    targetButtons.forEach((button) => {
      button.disabled = isPending;
      button.classList.toggle('is-loading', isPending);
    });
  }

  setNotice(message, isSuccess = false) {
    if (!this.noticeEl) return;
    this.noticeEl.textContent = message || '';
    this.noticeEl.classList.toggle('is-success', Boolean(isSuccess && message));
  }

  async refreshSections(resource) {
    const sections = this.sectionsParam
      .split(',')
      .map((section) => section.trim())
      .filter(Boolean);

    if (!sections.length) return;

    const sectionsPayload = resource?.sections;

    const payload = sectionsPayload
      ? sectionsPayload
      : await fetch(`/?sections=${encodeURIComponent(sections.join(','))}`)
          .then((res) => res.json())
          .catch(() => ({}));

    const eventDetail = {
      resource,
      sourceId: 'cart-gift',
      data: {
        source: 'cart-gift',
        sections: payload,
      },
    };

    const cartUpdateEvent = new CustomEvent('cart:update', {
      bubbles: true,
      detail: eventDetail,
    });

    const themeCartUpdateEvent = new CustomEvent('theme:cart-update', {
      bubbles: true,
      detail: eventDetail,
    });

    document.dispatchEvent(cartUpdateEvent);
    document.dispatchEvent(themeCartUpdateEvent);
  }

  debugState(log = true) {
    const state = {
      thresholdCents: this.thresholdCents,
      collapsed: this.classList.contains('cart-gift--collapsed'),
      expanded: this.classList.contains('cart-gift--expanded'),
      pending: this.pending,
      autoRemoving: this.autoRemoving,
      hasAddButton: Boolean(this.addButton),
      hasRemoveButton: Boolean(this.removeButton),
    };

    if (log) {
      console.table(state);
    }

    return state;
  }
}

if (!customElements.get('cart-gift')) {
  customElements.define('cart-gift', CartGift);
}
